import { Directive, Injectable, inject } from '@angular/core';

import { EMPTY, Subject } from 'rxjs';

import type {
  ConfigPlugins,
  EditorButton,
  IProsemirrorConfig,
  PluginsButtons,
  PluginsEvents,
} from '@core/prosemirror-legacy';
import {
  AutoLinkWrapPlugin,
  BasicPlugin,
  BoldPlugin,
  EmojiEditorPlugin,
  ItalicPlugin,
  LinkEditPlugin,
  ListPlugin,
  RtSchema,
  SCHEMAS,
  UnderlinePlugin,
} from '@core/prosemirror-legacy';
import { ProsemirrorBaseConfig, ScrollStream$ } from '@core/prosemirror-legacy/angular';
import { SymbolPickerService } from '@core/symbol-legacy';

import { ListMenuService } from '../components/list-menu/list-menu.service';
import { ToolbarService } from '../components/toolbar/toolbar.service';
import { LinkComponentService } from '../components/tooltips/link/link-component.service';
import { ProsemirrorConfig } from '../providers';

export type ProsemirrorPositionDescriptionConfigEvents = PluginsEvents<
  ConfigPlugins<ProsemirrorPositionDescriptionConfig>
>;

@Injectable()
export class ProsemirrorPositionDescriptionConfig
  extends ProsemirrorBaseConfig
  implements IProsemirrorConfig
{
  protected readonly listMenuService = inject(ListMenuService, { optional: true });
  protected readonly symbolPickerService? = inject(SymbolPickerService, { optional: true });
  protected readonly toolbarService? = inject(ToolbarService, { optional: true });
  protected readonly linkService? = inject(LinkComponentService, { optional: true });
  protected readonly scrollStream$? = inject(ScrollStream$, { optional: true });

  public eventStream$ = new Subject<ProsemirrorPositionDescriptionConfigEvents>();

  private scroll$ = this.scrollStream$ || EMPTY;

  public getSchema() {
    return SCHEMAS[RtSchema.PositionDescription](document);
  }

  public getRenderPlugins() {
    return [];
  }

  public getEditorPlugins() {
    return [
      new BasicPlugin(),
      new EmojiEditorPlugin(this.symbolPickerService!, this.scroll$),
      new BoldPlugin(),
      new ItalicPlugin(),
      new UnderlinePlugin(),
      new LinkEditPlugin(this.scroll$, this.linkService!),
      new AutoLinkWrapPlugin(),
      new ListPlugin(this.listMenuService!),
    ];
  }

  public registerToolbarButtons(
    buttons: PluginsButtons<ConfigPlugins<ProsemirrorPositionDescriptionConfig>>,
  ) {
    const leftButtons: EditorButton[][] = [
      [buttons.toggleBold, buttons.toggleItalic, buttons.toggleUnderline],
      [buttons.toggleListMenu, buttons.toggleLink],
    ];
    const rightButtons: EditorButton[] = [buttons.toggleEmojiList];

    this.toolbarService!.setLeftButtons(leftButtons);
    this.toolbarService!.setRightButtons(rightButtons);
  }
}

@Directive({
  selector: '[prosemirrorPositionDescriptionConfig]',
  providers: [
    ProsemirrorPositionDescriptionConfig,
    { provide: ProsemirrorConfig, useValue: ProsemirrorPositionDescriptionConfig },
  ],
  exportAs: 'prosemirrorConfig',
  standalone: true,
})
export class ProsemirrorPositionDescriptionConfigDirective {}
